import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// 添加类型定义
interface TestimonialItem {
  key: string;
  avatar: string;
  industry: string;
}

interface HighlightItem {
  title: string;
  description: string;
}

export const InformationSections: React.FC = () => {
  const { t, i18n } = useTranslation();

  // 将 testimonialData 移到组件内部
  const testimonialData: TestimonialItem[] = [
    {
      key: 'developer',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      industry: 'technology'
    },
    {
      key: 'marketer',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      industry: 'marketing'
    },
    {
      key: 'doctor',
      avatar: 'https://randomuser.me/api/portraits/men/86.jpg',
      industry: 'healthcare'
    },
    {
      key: 'designer',
      avatar: 'https://randomuser.me/api/portraits/women/29.jpg',
      industry: 'design'
    },
    {
      key: 'researcher',
      avatar: 'https://randomuser.me/api/portraits/men/95.jpg',
      industry: 'research'
    },
    {
      key: 'ecommerce',
      avatar: 'https://randomuser.me/api/portraits/women/76.jpg',
      industry: 'ecommerce'
    }
  ];

  // 修复 t() 返回值的类型问题
  const whyChooseFeatures = (t('sections.whyChoose.features.list', { returnObjects: true }) as string[]);
  const highlights = (t('sections.whyChoose.highlights', { returnObjects: true }) as HighlightItem[]);
  const faqItems = (t('sections.faq.items', { returnObjects: true }) as Record<string, { question: string; answer?: string }>);

  const renderTestimonials = () => {
    return (
      <section className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 sm:mb-8 bg-gradient-to-r from-primary-light to-primary-dark bg-clip-text text-transparent">
          {t('sections.testimonials.title')}
        </h2>
        {/* 用户评价网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonialData.map((item) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <img 
                  src={item.avatar}
                  alt={t(`sections.testimonials.${item.key}.name`)}
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                  width="48"
                  height="48"
                />
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {t(`sections.testimonials.${item.key}.name`)}
                    </h4>
                    <span className="ml-auto text-xs px-2 py-1 bg-primary-light/10 text-primary-dark rounded-full">
                      {t(`industries.${item.industry}`)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {t(`sections.testimonials.${item.key}.role`)}
                  </p>
                  <p className="text-sm text-gray-700 italic">
                    "{t(`sections.testimonials.${item.key}.content`)}"
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 添加科技感装饰 */}
        <div className="relative mt-12 border-t border-gray-100">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-8">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary-light"></div>
              <div className="w-2 h-2 rounded-full bg-primary"></div>
              <div className="w-2 h-2 rounded-full bg-primary-dark"></div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderFAQ = () => {
    return (
      <section className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 sm:mb-8 bg-gradient-to-r from-primary-light to-primary-dark bg-clip-text text-transparent">
          {t('sections.faq.title')}
        </h2>

        {/* 装饰性元素 */}
        <div className="flex justify-center items-center gap-8 mb-8">
          <div className="h-1 flex-1 bg-gradient-to-r from-primary-light/10 to-primary/10 rounded-full"></div>
          <div className="w-3 h-3 rounded-full bg-primary-light/20"></div>
          <div className="h-1 flex-1 bg-gradient-to-r from-primary/10 to-primary-dark/10 rounded-full"></div>
        </div>

        {/* FAQ 网格 */}
        <div className="grid grid-cols-1 gap-6">
          {/* 第一行：What + When */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-1">
              {faqItems['what'] && renderFAQItem('what', faqItems['what'])}
            </div>
            <div className="md:col-span-1">
              {faqItems['when'] && renderFAQItem('when', faqItems['when'])}
            </div>
          </div>
          
          {/* 第二行：How */}
          <div className="w-full">
            {faqItems['how'] && renderFAQItem('how', faqItems['how'])}
          </div>
          
          {/* 第三行：Secure + Free */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-1">
              {faqItems['secure'] && renderFAQItem('secure', faqItems['secure'])}
            </div>
            <div className="md:col-span-1">
              {faqItems['free'] && renderFAQItem('free', faqItems['free'])}
            </div>
          </div>
        </div>

        {/* 科技感装饰 */}
        <div className="relative mt-12 border-t border-gray-100">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-8">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary-light"></div>
              <div className="w-2 h-2 rounded-full bg-primary"></div>
              <div className="w-2 h-2 rounded-full bg-primary-dark"></div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderFAQItem = (key: string, faq: { question: string; answer?: string } | undefined) => {
    if (!faq) return null;

    const baseClasses = "bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 h-full";

    if (key === 'how') {
      const currentLang = i18n.language;
      const langConfig = {
        // 默认配置，用于未明确定义的语言
        default: {
          intro: t('sections.faq.items.how.intro'),
          steps: [
            t('sections.faq.items.how.step1'),
            t('sections.faq.items.how.step2'),
            t('sections.faq.items.how.step3'),
            t('sections.faq.items.how.step4')
          ]
        }
      };

      const config = langConfig[currentLang as keyof typeof langConfig] || langConfig.default;

      return (
        <motion.div
          key={key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${baseClasses} md:col-span-2`}
        >
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-light to-primary-dark flex items-center justify-center text-white shrink-0">
              <span className="text-lg">Q</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {faq.question}
              </h3>
              <div className="space-y-4">
                <p className="text-gray-600 leading-relaxed">
                  {config.intro}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {config.steps.map((step, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm"
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-light/10 text-primary-dark flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <p className="text-gray-600 text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={baseClasses}
      >
        <div className="flex items-start gap-4">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-light to-primary-dark flex items-center justify-center text-white shrink-0">
            <span className="text-lg">Q</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {faq.question}
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {faq.answer}
            </p>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderWhyChooseUs = () => {
    return (
      <section aria-labelledby="why-choose-title">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 sm:mb-8 bg-gradient-to-r from-primary-light to-primary-dark bg-clip-text text-transparent">
          {t('sections.whyChoose.title')}
        </h2>
        
        <p className="text-gray-600 mb-6 sm:mb-12 text-center text-base sm:text-lg max-w-2xl mx-auto">
          {t('sections.whyChoose.intro')}
        </p>

        {/* Feature Grid - 移动端双列 */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-12">
          {whyChooseFeatures.map((feature: string, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex flex-col items-center p-3 sm:p-6 bg-gradient-to-br from-white to-gray-50 rounded-lg sm:rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:transform hover:scale-105"
            >
              <span className="text-lg sm:text-xl mb-1 sm:mb-2">{feature.split(' ')[0]}</span>
              <span className="text-xs sm:text-sm text-gray-600 text-center">
                {feature.split(' ').slice(1).join(' ')}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Highlights - 移动端单列 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-6 sm:mb-12">
          {highlights.map((highlight: HighlightItem, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 sm:p-6 bg-gradient-to-br from-primary-light/5 via-primary/5 to-primary-dark/5 rounded-lg sm:rounded-xl border border-gray-100 hover:border-primary-light/30 transition-all duration-300"
            >
              <div className="flex items-center mb-2 sm:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary-light to-primary-dark flex items-center justify-center text-white text-lg sm:text-xl mr-3 sm:mr-4">
                  {index + 1}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-primary-dark">
                  {highlight.title}
                </h3>
              </div>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {highlight.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Conclusion */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="bg-gradient-to-r from-primary-light/10 via-primary/10 to-primary-dark/10 p-4 sm:p-8 rounded-lg sm:rounded-2xl">
            <p className="text-base sm:text-lg text-gray-700 font-medium italic leading-relaxed">
              {t('sections.whyChoose.conclusion')}
            </p>
          </div>
        </motion.div>
      </section>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* FAQ Section */}
        <section aria-labelledby="faq-title">
          {renderFAQ()}
        </section>

        {/* Why Choose Us Section */}
        {renderWhyChooseUs()}

        {/* Testimonials Section */}
        <section aria-labelledby="testimonials-title">
          {renderTestimonials()}
        </section>
      </div>
    </div>
  );
}; 